import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import {
  Grid,
  Checkbox,
  TextField,
  Typography,
  Chip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import moment from "moment-timezone";
import SingleSelect from "components/Select/SingleSelect";
import { setDateRangePicTreeHokuden, setDateRangePicTreeMinMax, setSelectedSeasonHokuden, getSeasonDataHokuden } from "actions";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const HOKUDEN_GROUP_SINGLE_SEASON_OPTIONS = [
  { name: "PicTréeまちバトル in 北海道 2026夏", value: "cmr0bus0w00010azches0f7ak" },
  { name: "PicTréeまちバトル in 北海道 2025夏", value: "cmb68qvei00011vo0e591229z" },
];
const HOKUDEN_DEFAULT_SINGLE_SEASON_OPTION = {
  name: "PicTréeまちバトル in 北海道 2026夏",
  value: "cmr0bus0w00010azches0f7ak",
};
const HOKUDEN_DEFAULT_SINGLE_SEASON_OPTIONS = HOKUDEN_GROUP_SINGLE_SEASON_OPTIONS;
const HOKUDEN_GROUP_PAGE = "/game/pictree/hokuden/photos/group";

const useStyles = makeStyles((theme) =>
  createStyles({
    calendarWrapper: {
      paddingLeft: 8,
    },
    root: {
      backgroundColor: "#fafafa",
      zIndex: 1000,
      paddingTop: 16,
      paddingLeft: 8,
      marginTop: 0,
    },
    chip: {
      margin: theme.spacing(0.5),
      fontSize: "0.75rem",
      height: 24,
    },
    autocomplete: {
      width: 510,
      marginTop: -8,
    },
    seasonTextField: {
      "& .MuiOutlinedInput-root": {
        padding: "1px 8px",
      },
      "& .MuiOutlinedInput-input": {
        height: "1.2em",
      },
    },
  })
);

const PeriodHokuden = ({
  setDateRangePicTreeHokuden,
  setDateRangePicTreeMinMax,
  setSelectedSeasonHokuden,
  getSeasonDataHokuden,
  seasonData,
  selectedSeason,
  dateRangePicTreeHokuden,
  singlePeriod,
  currentPage,
}) => {
  const classes = useStyles();
  const currentDateTime = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const today = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const todayFormatted = moment(today).format("YYYY-MM-DD");
  const isGroupPage = currentPage === HOKUDEN_GROUP_PAGE;
  const singleSeasonOptions = isGroupPage
    ? HOKUDEN_GROUP_SINGLE_SEASON_OPTIONS
    : HOKUDEN_DEFAULT_SINGLE_SEASON_OPTIONS;

  const [seasonOption, setSeasonOption] = useState(HOKUDEN_DEFAULT_SINGLE_SEASON_OPTION);

  useEffect(() => {
    getSeasonDataHokuden();
  }, []);

  useEffect(() => {
    if (seasonData.length > 0) {
      if (singlePeriod && isGroupPage && (!selectedSeason || selectedSeason.length === 0)) {
        const defaultSeason = seasonData.find(
          (season) => season.seasonId === HOKUDEN_DEFAULT_SINGLE_SEASON_OPTION.value
        ) || seasonData[0];
        if (defaultSeason) {
          setSeasonOption(HOKUDEN_DEFAULT_SINGLE_SEASON_OPTION);
          setSelectedSeasonHokuden([defaultSeason]);
          setDateRangePicTreeMinMax([
            defaultSeason.seasonStart.split(" ")[0],
            defaultSeason.seasonEnd.split(" ")[0],
          ]);
          setDateRangePicTreeHokuden([
            defaultSeason.seasonStart.split(" ")[0],
            defaultSeason.seasonEnd.split(" ")[0],
          ]);
          return;
        }
      }

      const preferredOption = singleSeasonOptions.find(
        (option) => option.value === seasonOption?.value
      );
      const preferredSeason = preferredOption
        ? seasonData.find((season) => season.seasonId === preferredOption.value)
        : null;
      const selectedSeasonId = selectedSeason?.[0]?.seasonId;
      const selectedSeasonIsAllowed = singleSeasonOptions.some(
        (option) => option.value === selectedSeasonId
      );

      if (singlePeriod && !isGroupPage && !selectedSeasonIsAllowed) {
        const nextSeason = preferredSeason || seasonData.find((season) => season.seasonId === HOKUDEN_DEFAULT_SINGLE_SEASON_OPTION.value);
        if (nextSeason) {
          setSeasonOption(HOKUDEN_DEFAULT_SINGLE_SEASON_OPTION);
          setSelectedSeasonHokuden([nextSeason]);
          setDateRangePicTreeHokuden([
            nextSeason.seasonStart.split(" ")[0],
            nextSeason.seasonEnd.split(" ")[0],
          ]);
          setDateRangePicTreeMinMax([
            nextSeason.seasonStart.split(" ")[0],
            nextSeason.seasonEnd.split(" ")[0],
          ]);
          return;
        }
      }

      const matchedSeasons = seasonData.filter((season) => {
        const startDate = season.seasonStart;
        const endDate = season.seasonEnd;
        return today >= startDate && today <= endDate;
      });

      if (!selectedSeason || selectedSeason.length === 0) {
        if (matchedSeasons.length > 0) {
          setSelectedSeasonHokuden(matchedSeasons);
          const earliestStart = matchedSeasons
            .map((s) => s.seasonStart)
            .reduce((a, b) => (a < b ? a : b));
          const latestEnd = matchedSeasons
            .map((s) => s.seasonEnd)
            .reduce((a, b) => (a > b ? a : b));

          // check end date if before today
          const defaultMaxDateFormatted = moment(latestEnd).format('YYYY-MM-DD');
          const isEndDateAfterToday = moment(today).isBefore(defaultMaxDateFormatted);
          if (isEndDateAfterToday) {
            setDateRangePicTreeMinMax([
              earliestStart.split(" ")[0],
              todayFormatted,
            ]);
            setDateRangePicTreeHokuden([
              earliestStart.split(" ")[0],
              todayFormatted,
            ]);
          } else {
            setDateRangePicTreeMinMax([
              earliestStart.split(" ")[0],
              latestEnd.split(" ")[0],
            ]);
            setDateRangePicTreeHokuden([
              earliestStart.split(" ")[0],
              latestEnd.split(" ")[0],
            ]);
          }
        } else if (!isGroupPage) {
          setSelectedSeasonHokuden([seasonData[0]]);
          setDateRangePicTreeHokuden([
            seasonData[0]["seasonStart"].split(" ")[0],
            seasonData[0]["seasonEnd"].split(" ")[0],
          ]);
        }
      }
    }
  }, [
    seasonData,
    currentPage,
    selectedSeason,
    seasonOption,
    singlePeriod,
    setDateRangePicTreeHokuden,
    setDateRangePicTreeMinMax,
    setSelectedSeasonHokuden,
    today,
    todayFormatted,
    isGroupPage,
    singleSeasonOptions,
  ]);

  const handleSelectionChange = (event, value) => {
    const uniqueValue = value.filter((option, index, self) => {
      const isDuplicate =
        self.filter((item) => item.seasonId === option.seasonId).length > 1;
      return !isDuplicate;
    });

    const validSelections = uniqueValue.filter(
      (option, index, self) =>
        // !moment(option.seasonStart).isAfter(currentDateTime) &&
        self.findIndex((o) => o.seasonId === option.seasonId) === index
    );

    if (validSelections.length > 0) {
      const selectedSeasonsData = seasonData.filter((s) =>
        validSelections.some((item) => item.seasonId === s.seasonId)
      );

      const earliestStart = selectedSeasonsData
        .map((s) => s.seasonStart)
        .reduce((a, b) => (a < b ? a : b));

      const latestEnd = selectedSeasonsData
        .map((s) => s.seasonEnd)
        .reduce((a, b) => (a > b ? a : b));

      setDateRangePicTreeMinMax([
        earliestStart.split(" ")[0],
        latestEnd.split(" ")[0],
      ]);
      setDateRangePicTreeHokuden([
        earliestStart.split(" ")[0],
        latestEnd.split(" ")[0],
      ]);

      // 範囲内のシーズンを取得
      // const autoSelectedSeasons = seasonData.filter(
      //   (s) =>
      //     moment(s.seasonStart).isSameOrAfter(earliestStart) &&
      //     moment(s.seasonEnd).isSameOrBefore(latestEnd)
      // );

      // // 現在の選択に追加
      // const updatedSelections = [...validSelections, ...autoSelectedSeasons].filter(
      //   (option, index, self) =>
      //     self.findIndex((o) => o.seasonId === option.seasonId) === index
      // );

      // ステートを更新
      setSelectedSeasonHokuden(validSelections);
    } else {
      setDateRangePicTreeMinMax([]);
      setDateRangePicTreeHokuden([]);
    }
  };

  const handleChange = (event) => {
    const nextOption = singleSeasonOptions.find(
      (option) => option.value === event.target.value
    );
    if (!nextOption) {
      return;
    }

    setSeasonOption(nextOption);

    const nextSeason = seasonData.find((season) => season.seasonId === nextOption.value);
    if (!nextSeason) {
      return;
    }

    const startDate = nextSeason.seasonStart.split(" ")[0];
    const endDate = nextSeason.seasonEnd.split(" ")[0];
    setSelectedSeasonHokuden([nextSeason]);
    setDateRangePicTreeHokuden([startDate, endDate]);
    setDateRangePicTreeMinMax([startDate, endDate]);
  };

  return (
    <div className={classes.calendarWrapper}>
      <div className={classes.root}>
        <Grid container spacing={1} direction="row">
          <Grid item spacing={1}>
            シーズン：
          </Grid>
          <Grid item>
            {singlePeriod && (
              <div style={{ width: 300, marginTop: -12 }}>
                <SingleSelect
                  selected={seasonOption}
                  options={singleSeasonOptions}
                  handleChange={handleChange}
                />
              </div>
            )}
            {!singlePeriod &&
            <Autocomplete
              multiple
              id="season-autocomplete"
              options={seasonData}
              disableCloseOnSelect
              getOptionLabel={(option) =>
                `${option.seasonName} : ${option.seasonStart} ~ ${option.seasonEnd}`
              }
              renderOption={(option, { selected }) => {
                const isFutureSeason = moment(option.seasonStart).isAfter(currentDateTime);
                const isSelected = selectedSeason.some((selected) => selected.seasonId === option.seasonId);
                return (
                  <div>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={isSelected}
                      disabled={isFutureSeason}
                    />
                    {`${option.seasonName}: `}
                    <span
                      style={{ color: "gray", marginLeft: 16, fontSize: 13 }}
                    >
                      {`${option.seasonStart} ~ ${option.seasonEnd}`}
                    </span>
                  </div>
                );
              }}
              value={selectedSeason || []}
              onChange={handleSelectionChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder=""
                  className={classes.seasonTextField}
                />
              )}
              renderTags={(selected, getTagProps) => {
                const visibleTags = selected.slice(0, 3);
                const extraCount = selected.length - visibleTags.length;
                return [
                  ...visibleTags.map((option, index) => (
                    <Chip
                      key={index}
                      label={option.seasonName}
                      size="small"
                      className={classes.chip}
                    />
                  )),
                  extraCount > 0 && (
                    <Typography
                      key="extra"
                      variant="body2"
                      style={{ marginLeft: 8, color: "gray" }}
                    >
                      +{extraCount}
                    </Typography>
                  ),
                ];
              }}
              disableClearable
              className={classes.autocomplete}
            />
            }
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

PeriodHokuden.prototypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  periodType: state.pictree.periodType,
  seasonData: state.pictreeHokuden.seasonData,
  selectedSeason: state.pictreeHokuden.selectedSeason,
  dateRangePicTreeHokuden: state.page.dateRangePicTreeHokuden,
});

const mapDispatchToProps = (dispatch) => ({
  setDateRangePicTreeHokuden: (param) => dispatch(setDateRangePicTreeHokuden(param)),
  setDateRangePicTreeMinMax: (param) => dispatch(setDateRangePicTreeMinMax(param)),
  getSeasonDataHokuden: () => dispatch(getSeasonDataHokuden()),
  setSelectedSeasonHokuden: (param) => dispatch(setSelectedSeasonHokuden(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(PeriodHokuden));
